import React from 'react';
import { getPDFDocument } from '../utils/utils';
import { usePromise } from './usePromise';
import { ErrorType } from '@root/libs/data/src';
import { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';

type GetPDFDocumentParams = Parameters<typeof getPDFDocument>[0];

export function usePDFDocument(
  props: Omit<GetPDFDocumentParams, 'data'> & {
    data?: GetPDFDocumentParams['data'];
  } & {
    type?: string;
    prepareError?: ErrorType;
  }
) {
  const [pdfDocument, setPDFDocument] = React.useState<PDFDocumentProxy>();
  const [loadingTask, setLoadingTask] =
    React.useState<PDFDocumentLoadingTask>();
  const [documentError, setDocumentError] = React.useState<ErrorType>();

  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    const data = props.data;
    const type = props.type;

    if (data && type === 'PDF' && !props.prepareError && !isLoading) {
      console.log('usePDFDocument: Starting PDF document loading');
      console.log('usePDFDocument: data type:', typeof data);
      console.log(
        'usePDFDocument: data instanceof ArrayBuffer:',
        data instanceof ArrayBuffer
      );
      console.log(
        'usePDFDocument: data instanceof Uint8Array:',
        data instanceof Uint8Array
      );

      setIsLoading(true);
      const request = getPDFDocument({ data, onProgress: props.onProgress });
      request.pdfDocument
        .then((doc) => {
          console.log('usePDFDocument: PDF document loaded successfully');
          setIsLoading(false);
          setPDFDocument(doc);
        })
        .catch((err) => {
          console.error('usePDFDocument: PDF document loading failed:', err);
          setIsLoading(false);
          setDocumentError({ type: 'DocumentError', error: err });
          if (request.loadingTask && !request.loadingTask.destroyed)
            request.loadingTask.destroy();
        })
        .finally(() => {
          setIsLoading(false);
        });

      setLoadingTask(request.loadingTask);
      setDocumentError(request.error);
    }
    return () => {
      setIsLoading(false);
    };
  }, [props.data, props.type]);

  React.useEffect(() => {
    return () => {
      loadingTask && !loadingTask.destroyed && loadingTask.destroy();
    };
  }, [loadingTask]);

  React.useEffect(() => {
    if (!!props.prepareError && loadingTask && !loadingTask.destroyed) {
      loadingTask.destroy();
    }
  }, [!!props.prepareError, !!loadingTask]);

  React.useEffect(() => {
    if (documentError && loadingTask && !loadingTask.destroyed) {
      loadingTask.destroy();
    }
  }, [documentError]);

  return {
    isLoading,
    error: documentError,
    data: pdfDocument,
  };
}
